import { DebugKey, createDebug } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import type { ErrorBoundaryProps } from '@sentry/react';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

const DevelopmentFallback = ({
  error,
  componentStack
}: {
  error: unknown;
  componentStack: string | null;
}) => (
  <div
    style={{
      background: 'hsl(0, 100%, 97%)',
      padding: '15px',
      border: '1px solid hsl(0, 100%, 92%)',
      color: 'red',
      fontFamily: 'Menlo, monospace',
      fontSize: '11px',
      overflow: 'auto',
      whiteSpace: 'pre-wrap'
    }}
  >
    {`${error}`}
    {componentStack}
  </div>
);

const ErrorBoundary: React.FC<React.PropsWithChildren<ErrorBoundaryProps>> = ({
  children,
  fallback,
  beforeCapture,
  ...props
}) => {
  const debug = createDebug(DebugKey.Error);
  const brandId = useSelector(select.cms.getBrandId);
  const brandName = useSelector(select.cms.getBrandName);

  return (
    <Sentry.ErrorBoundary
      onError={(error, componentStack) => {
        debug(`caught rendering\n%s%s`, `${error}`, componentStack);
      }}
      fallback={
        process.env.NODE_ENV === 'production'
          ? fallback
          : ({ ...fallbackProps }) => <DevelopmentFallback {...fallbackProps} />
      }
      {...props}
      beforeCapture={(scope, ...rest) => {
        scope.setTag('app.brand.id', brandId);
        scope.setTag('app.brand.name', brandName);
        beforeCapture?.(scope, ...rest);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
